import type { CSSProperties } from "react"
import { memo, useLayoutEffect, useMemo, useRef, useState } from "react"
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import {
	restrictToHorizontalAxis,
	restrictToParentElement,
} from "@dnd-kit/modifiers"
import {
	arrayMove,
	horizontalListSortingStrategy,
	SortableContext,
	useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
	IconChevronDownOutline18 as ChevronDown,
	IconCodeOutline18 as Code,
	IconDotsOutline18 as Dots,
	IconGearOutline18 as Gear,
	IconPenOutline18 as Pen,
	IconPlusOutline18 as Plus,
	IconTabsPlusOutline18 as TabsPlus,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"

import type { HelpCenterTab } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import {
	AlertDialog,
	Button,
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuSeparator,
	ContextMenuTrigger,
	IconButton,
	NucleoFillIcon,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"

import type { GroupWithArticles } from "./types"
import type { DragEndEvent } from "@dnd-kit/core"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { useDocsTabDialogs } from "@/stores/docs/docs-tab-store"
import { DocsTabDialog } from "./docs-tab-dialog"
import { tabIconComponent } from "./tab-icon"
import {
	tabLandingArticleId,
	useDocsTabs,
	useSelectDocsTab,
} from "./use-docs-tabs"
import {
	publishTabAction,
	TAB_COPY,
	UnpublishedTabBadge,
	usePublishDocsTab,
} from "./use-publish-docs-tab"

const TAB_CLASS =
	"flex h-7 flex-none cursor-default select-none items-center gap-1.5 rounded-md px-2 text-sm font-medium"

// The menu button sits beside the tab, not in it, so the tab lights up from their
// shared wrapper too, and stays lit while that menu is open.
const INACTIVE_TAB = cn(
	"text-secondary hover:bg-gray-100 hover:text-primary dark:hover:bg-gray-800/50",
	"group-hover/tab:bg-gray-100 group-hover/tab:text-primary dark:group-hover/tab:bg-gray-800/50",
	"group-has-[[data-state=open]]/tab:bg-gray-100 group-has-[[data-state=open]]/tab:text-primary dark:group-has-[[data-state=open]]/tab:bg-gray-800/50",
)

function useCanEdit() {
	const { role } = useRole()
	return !!role && role !== "VIEWER"
}

/** The new order after a drag, as the `reorder` mutator takes it. */
export function reorderedTabs(
	tabs: readonly Pick<HelpCenterTab, "id">[],
	activeId: string,
	overId: string,
): { id: string; order: number }[] | null {
	const from = tabs.findIndex((tab) => tab.id === activeId)
	const to = tabs.findIndex((tab) => tab.id === overId)
	if (from === -1 || to === -1 || from === to) return null
	return arrayMove([...tabs], from, to).map((tab, order) => ({
		id: tab.id,
		order,
	}))
}

const TAB_GAP = 2 // gap-0.5
const ROW_GAP = 8 // gap-2

const MORE_CLASS =
	"h-7 min-h-7 gap-1 rounded-md px-2 text-sm data-[state=open]:bg-gray-100 dark:data-[state=open]:bg-gray-800"

// Ten rows and part of an eleventh, so the cut-off row says the list scrolls.
const MENU_SCROLL_CLASS = "max-h-[21.5rem] overflow-y-auto pb-2"

const DEFAULT_TAB = { name: "Documentation", icon: "BookOpen" }

/**
 * Which tabs fit in `available` pixels, by index. When they don't all fit, room
 * is kept for the "More" button, and the open tab always stays in the row.
 */
export function fitTabs({
	widths,
	available,
	gap,
	overflowWidth,
	activeIndex,
}: {
	widths: readonly number[]
	available: number
	gap: number
	overflowWidth: number
	activeIndex: number
}): number[] {
	const all = widths.map((_, index) => index)
	const span = (indexes: readonly number[]) =>
		indexes.reduce((sum, index) => sum + (widths[index] ?? 0), 0) +
		gap * Math.max(indexes.length - 1, 0)
	// Not laid out yet (or in a test): nothing to measure against.
	if (available <= 0 || span(all) <= available) return all

	const budget = available - overflowWidth - gap
	const visible: number[] = []
	for (const index of all) {
		if (span([...visible, index]) > budget) break
		visible.push(index)
	}
	if (activeIndex < 0 || visible.includes(activeIndex)) return visible
	while (visible.length > 0 && span([...visible, activeIndex]) > budget) {
		visible.pop()
	}
	return [...visible, activeIndex].sort((a, b) => a - b)
}

/**
 * The docs tabs, in the page's header row. A tab opens the page readers land on.
 * The ones that don't fit move into a "More" menu.
 */
export const DocsTabBar = memo(
	({ articleGroups }: { articleGroups: readonly GroupWithArticles[] }) => {
		const zero = useZero()
		const canEdit = useCanEdit()
		const { tabs, activeTabId } = useDocsTabs()
		const selectTab = useSelectDocsTab()
		// A press only becomes a drag after a few pixels, so a click still opens the tab.
		const sensors = useSensors(
			useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		)
		const openTab = (tab: HelpCenterTab | null) =>
			selectTab(tab, tabLandingArticleId(articleGroups, tabs, tab))

		// Index 0 is the default tab, then the workspace's own tabs in order.
		const entries = useMemo(() => [null, ...tabs], [tabs])
		const activeIndex =
			activeTabId === null
				? 0
				: tabs.findIndex((tab) => tab.id === activeTabId) + 1
		const rowRef = useRef<HTMLDivElement>(null)
		const measureRef = useRef<HTMLDivElement>(null)
		const addRef = useRef<HTMLDivElement>(null)
		const [fitted, setFitted] = useState<number[] | null>(null)

		useLayoutEffect(() => {
			const row = rowRef.current
			const measure = measureRef.current
			if (!row || !measure) return
			const fit = () => {
				const copies = [...measure.children] as HTMLElement[]
				const more = copies.pop()
				const add = addRef.current
				const next = fitTabs({
					widths: copies.map((copy) => copy.offsetWidth),
					available: row.clientWidth - (add ? add.offsetWidth + ROW_GAP : 0),
					gap: TAB_GAP,
					overflowWidth: (more?.offsetWidth ?? 0) + TAB_GAP,
					activeIndex,
				})
				setFitted((prev) => (prev && prev.join() === next.join() ? prev : next))
			}
			fit()
			if (typeof ResizeObserver === "undefined") return
			const observer = new ResizeObserver(fit)
			observer.observe(row)
			return () => observer.disconnect()
		}, [entries, activeIndex])

		const shown = new Set(fitted ?? entries.map((_, index) => index))
		const shownTabs = tabs.filter((_, index) => shown.has(index + 1))
		const overflow = entries.filter((_, index) => !shown.has(index))

		const onDragEnd = ({ active, over }: DragEndEvent) => {
			if (!over) return
			const items = reorderedTabs(tabs, String(active.id), String(over.id))
			if (!items) return
			void mutateOk(
				zero.mutate(mutators.helpCenterTabs.reorder({ items })),
			).catch((error: unknown) =>
				toast.error({
					title: "Couldn't move the tab",
					description: error instanceof Error ? error.message : undefined,
				}),
			)
		}

		const moreButton = (
			<Button variant="ghost" iconRight={ChevronDown} className={MORE_CLASS}>
				More
			</Button>
		)

		return (
			<div
				ref={rowRef}
				className="relative flex min-w-0 flex-1 items-center gap-2"
			>
				{/* Every tab at its natural width, so the row knows what fits. */}
				<div
					ref={measureRef}
					aria-hidden
					inert
					className="pointer-events-none invisible absolute left-0 top-0 flex h-0 gap-0.5 overflow-hidden"
				>
					{entries.map((tab) => (
						<span key={tab?.id ?? "default"} className={TAB_CLASS}>
							<TabLabel
								name={tab?.name ?? DEFAULT_TAB.name}
								icon={tab?.icon ?? DEFAULT_TAB.icon}
								isPublished={tab?.isPublished ?? true}
							/>
						</span>
					))}
					{moreButton}
				</div>
				<div className="flex min-w-0 items-center gap-0.5">
					<div
						role="tablist"
						aria-label="Docs tabs"
						className="flex min-w-0 items-center gap-0.5"
					>
						{shown.has(0) && (
							<TabButton
								name={DEFAULT_TAB.name}
								icon={DEFAULT_TAB.icon}
								isActive={activeTabId === null}
								onSelect={() => openTab(null)}
							/>
						)}
						<DndContext
							sensors={sensors}
							onDragEnd={onDragEnd}
							modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
						>
							<SortableContext
								items={shownTabs.map((tab) => tab.id)}
								strategy={horizontalListSortingStrategy}
								disabled={!canEdit}
							>
								{shownTabs.map((tab) => (
									<CustomTab
										key={tab.id}
										tab={tab}
										hasGroups={articleGroups.some(
											(group) => group.tabId === tab.id,
										)}
										isActive={tab.id === activeTabId}
										onSelect={() => openTab(tab)}
									/>
								))}
							</SortableContext>
						</DndContext>
					</div>
					{overflow.length > 0 && (
						<DropdownMenu
							className={cn("w-60", MENU_SCROLL_CLASS)}
							contentProps={{ align: "start" }}
							trigger={moreButton}
						>
							{overflow.map((tab) => (
								<DropdownMenuItem
									key={tab?.id ?? "default"}
									Icon={tabIconComponent(tab?.icon ?? DEFAULT_TAB.icon)}
									onSelect={() => openTab(tab)}
								>
									<span className="truncate">
										{tab?.name ?? DEFAULT_TAB.name}
									</span>
									{tab && !tab.isPublished && (
										<span className="ml-auto pl-2">
											<UnpublishedTabBadge />
										</span>
									)}
								</DropdownMenuItem>
							))}
						</DropdownMenu>
					)}
				</div>
				<RoleCheck not={["VIEWER"]}>
					<div ref={addRef} className="flex flex-none items-center">
						<AddTabMenu
							hasApiTab={tabs.some((t) => t.kind === "API_REFERENCE")}
						/>
					</div>
				</RoleCheck>
			</div>
		)
	},
)

/**
 * What deleting a tab does to its content. The first tab is always published,
 * so an unpublished tab's groups go live there, and the dialog says so.
 */
export function deleteTabDescription({
	isApi,
	isPublished,
	hasGroups,
}: {
	isApi: boolean
	isPublished: boolean
	hasGroups: boolean
}) {
	const groups =
		isPublished || !hasGroups
			? "Its groups and articles move back to the first tab."
			: TAB_COPY.deleteGoesLive
	if (!isApi) return groups
	const api =
		"Its endpoint pages go offline. You can import the spec again later."
	return hasGroups ? `${api} ${groups}` : api
}

/** A tab's icon and name: the tab, and its copy that measures it, draw the same. */
function TabLabel({
	name,
	icon,
	iconClassName,
	isPublished = true,
}: {
	name: string
	icon: string
	iconClassName?: string
	isPublished?: boolean
}) {
	return (
		<>
			<NucleoFillIcon
				icon={icon}
				className={cn("size-3.5 flex-none", iconClassName)}
			/>
			<span className="max-w-48 truncate">{name}</span>
			{!isPublished && <UnpublishedTabBadge />}
		</>
	)
}

function TabButton({
	name,
	icon,
	isActive,
	onSelect,
	iconClassName,
	isPublished,
}: {
	name: string
	icon: string
	isActive: boolean
	onSelect: () => void
	iconClassName?: string
	isPublished?: boolean
}) {
	return (
		<button
			type="button"
			role="tab"
			aria-selected={isActive}
			onClick={onSelect}
			className={cn(
				TAB_CLASS,
				isActive ? "bg-gray-100 text-primary dark:bg-gray-800" : INACTIVE_TAB,
			)}
		>
			<TabLabel
				name={name}
				icon={icon}
				iconClassName={iconClassName}
				isPublished={isPublished}
			/>
		</button>
	)
}

/**
 * A tab editors drag to reorder. Its actions open from a menu that takes the
 * icon's place on hover, or from a right-click.
 */
const CustomTab = memo(
	({
		tab,
		hasGroups,
		isActive,
		onSelect,
	}: {
		tab: HelpCenterTab
		hasGroups: boolean
		isActive: boolean
		onSelect: () => void
	}) => {
		const zero = useZero()
		const canEdit = useCanEdit()
		const selectTab = useSelectDocsTab()
		const { setApiSettingsTabId } = useDocsTabDialogs()
		const [isEditOpen, setIsEditOpen] = useState(false)
		const [isDeleteOpen, setIsDeleteOpen] = useState(false)
		const isApi = tab.kind === "API_REFERENCE"
		const removeApiReference = api.apiReference.delete.useMutation()
		const publishTab = usePublishDocsTab()
		const {
			attributes,
			listeners,
			setNodeRef,
			transform,
			transition,
			isDragging,
		} = useSortable({ id: tab.id })
		const style: CSSProperties = {
			transform: CSS.Translate.toString(transform),
			transition,
		}

		if (!canEdit) {
			return (
				<TabButton
					name={tab.name}
					icon={tab.icon}
					isActive={isActive}
					onSelect={onSelect}
					isPublished={tab.isPublished}
				/>
			)
		}

		const actions = [
			{ label: "Edit", Icon: Pen, onSelect: () => setIsEditOpen(true) },
			...(isApi
				? [
						{
							label: "Settings",
							Icon: Gear,
							onSelect: () => setApiSettingsTabId(tab.id),
						},
					]
				: []),
			{
				...publishTabAction(tab),
				onSelect: () => void publishTab(tab, !tab.isPublished),
			},
		]

		return (
			<>
				<ContextMenu>
					<ContextMenuTrigger asChild>
						<div
							ref={setNodeRef}
							style={style}
							{...attributes}
							{...listeners}
							// The tab inside is the focus stop; the drag wrapper stays out of the tab order.
							role="presentation"
							tabIndex={-1}
							className={cn(
								"group/tab relative flex flex-none items-center",
								isDragging && "z-10 opacity-80",
							)}
						>
							<TabButton
								name={tab.name}
								icon={tab.icon}
								isActive={isActive}
								onSelect={onSelect}
								isPublished={tab.isPublished}
								// The menu button covers the icon, so the icon steps aside for it.
								iconClassName="group-hover/tab:opacity-0 group-has-[[data-state=open]]/tab:opacity-0 group-has-[[aria-haspopup]:focus-visible]/tab:opacity-0"
							/>
							{/* Out of the layout and over the icon, so the tab keeps its width. */}
							<span
								className="absolute left-[5px] top-1/2 flex -translate-y-1/2"
								// A press on the menu must not pick the tab up for a drag.
								onPointerDown={(e) => e.stopPropagation()}
							>
								<DropdownMenu
									className="w-48"
									contentProps={{ align: "start" }}
									trigger={
										<IconButton
											Icon={Dots}
											variant="dark-ghost"
											size="sm"
											aria-label={`${tab.name} actions`}
											iconClassName="h-3 w-3"
											className="rounded opacity-0 focus-visible:opacity-100 group-hover/tab:opacity-100 data-[state=open]:bg-gray-200 data-[state=open]:opacity-100 dark:data-[state=open]:bg-gray-700"
										/>
									}
								>
									{actions.map((action) => (
										<DropdownMenuItem
											key={action.label}
											Icon={action.Icon}
											onSelect={action.onSelect}
										>
											{action.label}
										</DropdownMenuItem>
									))}
									<DropdownMenuSeparator />
									<DropdownMenuItem
										Icon={Trash}
										onSelect={() => setIsDeleteOpen(true)}
									>
										Delete
									</DropdownMenuItem>
								</DropdownMenu>
							</span>
						</div>
					</ContextMenuTrigger>
					<ContextMenuContent className="w-48">
						{actions.map((action) => (
							<ContextMenuItem
								key={action.label}
								Icon={action.Icon}
								onSelect={action.onSelect}
							>
								{action.label}
							</ContextMenuItem>
						))}
						<ContextMenuSeparator />
						<ContextMenuItem
							Icon={Trash}
							onSelect={() => setIsDeleteOpen(true)}
						>
							Delete
						</ContextMenuItem>
					</ContextMenuContent>
				</ContextMenu>
				<DocsTabDialog
					isOpen={isEditOpen}
					onClose={() => setIsEditOpen(false)}
					tab={tab}
					portalInstanceId={tab.portalInstanceId ?? null}
					nextOrder={tab.order}
				/>
				<AlertDialog
					isOpen={isDeleteOpen}
					onOpenChange={setIsDeleteOpen}
					// Groups and articles survive, so this is no red-button delete.
					variant="positive"
					confirmText="Delete tab"
					title={`Delete "${tab.name}"?`}
					description={deleteTabDescription({
						isApi,
						isPublished: tab.isPublished,
						hasGroups,
					})}
					onCancel={() => setIsDeleteOpen(false)}
					onConfirm={async () => {
						try {
							// The server mutator refuses API references: their stored specs
							// and search documents go with them, which only tRPC cleans up.
							if (isApi) await removeApiReference.mutateAsync({ tabId: tab.id })
							else
								await mutateOk(
									zero.mutate(mutators.helpCenterTabs.delete({ id: tab.id })),
									"server",
								)
							if (isActive) selectTab(null)
						} catch (error) {
							toast.error({
								title: isApi
									? "Couldn't delete the API reference"
									: "Couldn't delete the tab",
								description: error instanceof Error ? error.message : undefined,
							})
						}
						setIsDeleteOpen(false)
					}}
				/>
			</>
		)
	},
)

function AddTabMenu({ hasApiTab }: { hasApiTab: boolean }) {
	const workspace = useZeroWorkspace()
	const { setNewTabOpen, setNewApiReferenceOpen } = useDocsTabDialogs()
	return (
		<div className="flex flex-none items-center">
			<DropdownMenu
				className="w-52"
				contentProps={{ align: "start" }}
				trigger={
					<IconButton
						Icon={Plus}
						variant="ghost"
						size="sm"
						aria-label="Add a tab"
						iconClassName="h-3 w-3"
						className="text-tertiary hover:bg-gray-100 dark:hover:bg-gray-800"
					/>
				}
			>
				<DropdownMenuItem
					Icon={TabsPlus}
					onSelect={() => {
						if (workspace?.helpCenterNavStyle === "NAVBAR") {
							toast.info({
								title: "Switch the tab style to add custom tabs",
								description:
									"The header link style keeps the API reference in the header and has no custom tabs.",
							})
							return
						}
						setNewTabOpen(true)
					}}
				>
					New tab
				</DropdownMenuItem>
				{!hasApiTab && (
					<DropdownMenuItem
						Icon={Code}
						onSelect={() => setNewApiReferenceOpen(true)}
					>
						API reference
					</DropdownMenuItem>
				)}
			</DropdownMenu>
		</div>
	)
}
